<template>
  <div>
    <h1 class="text-3xl font-normal mb-4">Programs</h1>
    <h2 class="text-xl font-normal mt-8">
      Search Programs
    </h2>
    <BaseAccordion>
      <template #trigger="{ setIsOpen, isOpen }">
        <div class="search-wrapper flex mt-4">
          <SearchInput
            v-model="searchQuery"
            placeholder="Search for programs..."
            :is-loading="isLoading"
          />
          <BaseButton
            v-if="!hasDepartmentFilter"
            aria-label="Advanced filters toggle"
            title="Advanced filters toggle"
            :aria-expanded="isOpen ? 'true' : 'false'"
            :type="isOpen ? 'primary' : 'secondary'"
            class="ml-4 bg-theme-navbar-background color-theme-navbar-link hover:opacity-80"
            @click="setIsOpen(!isOpen)"
          >
            <BaseIcon icon="filter" />
          </BaseButton>
        </div>
      </template>
      <template #content>
        <div class="bg-white mb-4 p-4 -mt-8">
          <span
            class="visually-hidden"
            role="status"
          >
            Advanced filters open
          </span>
          <h4 class="font-semibold pt-2">Advanced Search</h4>
          <DepartmentFilter v-model="departmentFilter" />
        </div>
      </template>
    </BaseAccordion>
    <h2 class="text-lg font-normal flex justify-between">
      Results
      <BasePagination
        v-if="total > pageSize"
        :current-page="currentPage"
        :total-pages="totalPages"
        @page-change="updatePage"
      />
    </h2>
    <p role="status">
      <span v-if="isLoading">Search in progress</span>
      <span v-else-if="!programs.length">No results</span>
      <span
        v-else
        class="visually-hidden"
      >{{ programs.length }} results found</span>
    </p>
    <div v-if="total">
      <BaseTable
        :rows="programsFormatted"
        @select="goToProgram"
      >
        <template #header>
          <th class="th">Name</th>
          <th class="th">Type</th>
          <th class="th">Level</th>
          <th class="th">Degree Designation</th>
        </template>
        <template #default="{ row: program }">
          <td>
            <NuxtLink :to="getProgramUrl(program)">
              {{ programDisplayHeading(program) }}
            </NuxtLink>
          </td>
          <td>
            {{ program.type }}
          </td>
          <td>
            {{ program.level }}
          </td>
          <td>
            {{ program.degreeDesignation }}
          </td>
        </template>
      </BaseTable>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import SearchInput from '@/components/SearchInput'
import DepartmentFilter from '@/components/DepartmentFilter'
import pageLayout from '@/mixins/pageLayoutMixin'
import usePrograms from '@/composables/usePrograms'
import useFeatureFlags from '@/composables/useFeatureFlags'
import { TITLE_INTERPOLATIONS } from '@/constants/featureFlags'
import { generateProgramLabel } from '@/utils/dataLabels'
import useInterpolation from '@/composables/useInterpolation'
import useMeta from '@/composables/useMeta'

export default {
  components: {
    SearchInput,
    DepartmentFilter,
  },
  mixins: [pageLayout],
  head() {
    return {
      title: `Programs | ${this.catalogWideMetaTitle}`,
      meta: [
        {
          hid: 'description',
          name: 'description',
          content: `Programs | ${this.catalogWideMetaTitle}`,
        },
      ],
    }
  },
  computed: {
    ...mapState('settings', ['school']),
  },
  setup(_, { root }) {
    const { enableTitleInterpolations } = useFeatureFlags({
      enableTitleInterpolations: TITLE_INTERPOLATIONS,
    })
    const { getInterpolatedFieldValue } = useInterpolation()
    const { catalogWideMetaTitle } = useMeta()

    const {
      updatePage,
      search,
      goToProgram,
      getProgramUrl,
      fetchData,
      programsFormatted,
      hasDepartmentFilter,
      totalPages,
      departmentFilter,
      programs,
      filters,
      total,
      currentPage,
      isLoading,
      searchQuery,
      pageSize,
    } = usePrograms(root)

    return {
      updatePage,
      search,
      goToProgram,
      getProgramUrl,
      fetchData,
      programsFormatted,
      hasDepartmentFilter,
      totalPages,
      departmentFilter,
      programs,
      filters,
      total,
      currentPage,
      isLoading,
      searchQuery,
      pageSize,
      enableTitleInterpolations,
      getInterpolatedFieldValue,
      catalogWideMetaTitle,
    }
  },
  watch: {
    'filters.department': 'search',
    '$route.query': 'fetchData',
  },
  mounted() {
    this.fetchData({ sortBy: 'name' })
  },
  methods: {
    programDisplayHeading(program) {
      return generateProgramLabel(program, this.school, {
        getInterpolatedFieldValue: this.enableTitleInterpolations
          ? this.getInterpolatedFieldValue
          : undefined,
      })
    },
  },
}
</script>

<style lang="css" scoped>
.search-wrapper /deep/ .card {
  display: flex !important;
  margin-bottom: 0;
}

@screen md {
  .programs-list {
    column-count: 2;
  }
}
</style>
